import { Controller } from "@hotwired/stimulus"
import React, { Suspense, useEffect } from "react"
import { render, unmountComponentAtNode } from "react-dom"
import { ModalContainer } from "web/components"
import { AppProviders, LibraryProviders } from "web/Providers"
import { useFields } from "web/hooks/data"
import useShowFieldModal from "web/app/Settings/Customization/Fields/Show"

function FieldModalProvider({ subjectType, topicId, onReady }) {
  const fields = useFields({
    subjectType,
    topicId,
    includeMultipleSubjectsFields: true,
  })

  const showFieldModal = useShowFieldModal()

  useEffect(() => {
    onReady({
      showField: async (fieldId) => {
        const field = fields.find((f) => f.id === fieldId)
        if (field) {
          await showFieldModal({ fields, field })
          document.dispatchEvent(new CustomEvent("field-modal:saved"))
        }
      },
      showNewField: async () => {
        await showFieldModal({
          fields,
          field: { kind: "Text" },
          isNew: true,
        })
        document.dispatchEvent(new CustomEvent("field-modal:saved"))
      },
    })
  }, [fields, showFieldModal, onReady])

  return null
}

export default class FieldModalController extends Controller {
  static values = {
    subjectType: String,
    topicId: Number,
  }

  initialize() {
    global.React = React
    this.modalActions = null
    this.reactContainer = null
  }

  disconnect() {
    if (this.reactContainer) {
      unmountComponentAtNode(this.reactContainer)
      this.reactContainer.remove()
    }
    document.removeEventListener("field-modal:saved", this.handleSaved)
  }

  connect() {
    const topicId = this.topicIdValue || null

    this.reactContainer = document.createElement("div")
    this.element.appendChild(this.reactContainer)

    render(
      <Suspense fallback={null}>
        <LibraryProviders>
          <AppProviders>
            <FieldModalProvider
              subjectType={this.subjectTypeValue}
              topicId={topicId}
              onReady={(actions) => {
                this.modalActions = actions
              }}
            />
            <ModalContainer />
          </AppProviders>
        </LibraryProviders>
      </Suspense>,
      this.reactContainer
    )

    document.addEventListener("field-modal:saved", this.handleSaved)
  }

  handleSaved = () => {
    const frame = document.querySelector("turbo-frame#fields_table")
    if (frame) {
      frame.src = window.location.href
    }
  }

  edit({ params: { fieldId } }) {
    if (this.modalActions) {
      this.modalActions.showField(fieldId)
    }
  }

  create() {
    if (this.modalActions) {
      this.modalActions.showNewField()
    }
  }
}
